import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { AdminPageHeader, Card, Input, Select, Stat, Spinner, ErrorBanner, Pagination, EmptyState, Tag } from "./_shared";
import { Search, ShieldAlert, ShieldCheck } from "lucide-react";

const RESULT_LABELS = {
  success: "Success",
  denied_not_allowlisted: "Denied (not allowlisted)",
  provider_error: "Provider error",
  missing_fields: "Missing fields",
  invalid_session: "Invalid session",
};
const RESULT_COLORS = {
  success: "#138808",
  denied_not_allowlisted: "#E3000F",
  provider_error: "#FF9933",
  missing_fields: "#FF9933",
  invalid_session: "#737373",
};

export const LoginAttempts = () => {
  const [result, setResult] = useState("all");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 50;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const params = { result, skip, limit };
      if (q) params.q = q;
      const r = await adminApi.get("/admin/login-attempts", { params });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load login attempts.");
    }
  }, [result, q, skip]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="login-attempts-page">
      <AdminPageHeader
        eyebrow="SECURITY"
        title="Admin login attempts"
        sub="Every sign-in attempt to the admin console — successful or not. Use this to spot brute force / probing."
      />

      <div className="grid grid-cols-3 gap-3">
        <Stat
          label="Success / 24h"
          value={data?.summary?.success_24h?.toLocaleString() ?? "0"}
          accent="#138808"
          ink="#FFFFFF"
          testId="la-stat-success"
        />
        <Stat
          label="Denied / 24h"
          value={data?.summary?.denied_24h?.toLocaleString() ?? "0"}
          accent="#E3000F"
          ink="#FFFFFF"
          testId="la-stat-denied-24h"
        />
        <Stat
          label="Denied / 7d"
          value={data?.summary?.denied_7d?.toLocaleString() ?? "0"}
          testId="la-stat-denied-7d"
        />
      </div>

      <Card>
        <div className="grid md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-7">
            <label className="block text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400 mb-2">Search</label>
            <div className="relative">
              <Input
                value={q}
                onChange={(e) => { setSkip(0); setQ(e.target.value); }}
                placeholder="Search email, name, or IP…"
                data-testid="la-search"
                className="pl-9"
              />
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" strokeWidth={3} />
            </div>
          </div>
          <div className="md:col-span-5">
            <label className="block text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400 mb-2">Result</label>
            <Select value={result} onChange={(e) => { setSkip(0); setResult(e.target.value); }} data-testid="la-filter">
              <option value="all">All results</option>
              {Object.entries(RESULT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
          </div>
        </div>
      </Card>

      {err && <ErrorBanner message={err} />}
      {!data && !err && <Spinner />}

      {data && data.attempts.length === 0 && (
        <EmptyState title="No attempts logged yet." sub="Sign-ins to the admin console will appear here." />
      )}

      {data && data.attempts.length > 0 && (
        <Card>
          <ul className="divide-y divide-[#1f1f1f]" data-testid="la-list">
            {data.attempts.map((a) => (
              <li key={a.id} className="py-3 flex items-start gap-3" data-testid={`la-row-${a.id}`}>
                {a.result === "success"
                  ? <ShieldCheck size={16} strokeWidth={3} style={{ color: "#138808" }} className="shrink-0 mt-0.5" />
                  : <ShieldAlert size={16} strokeWidth={3} style={{ color: "#E3000F" }} className="shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag color={RESULT_COLORS[a.result] || "#737373"} testId={`la-result-${a.id}`}>
                      {RESULT_LABELS[a.result] || a.result}
                    </Tag>
                    <span className="text-sm font-bold" data-testid={`la-email-${a.id}`}>
                      {a.email || <span className="text-neutral-500 italic">no email</span>}
                    </span>
                    {a.name && <span className="text-xs text-neutral-400">· {a.name}</span>}
                    <span className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500 ml-auto">
                      {a.created_at?.slice(0, 19).replace("T", " ")}
                    </span>
                  </div>
                  <p className="text-[11px] text-neutral-400 mt-1" data-testid={`la-ip-${a.id}`}>
                    IP: <span className="font-mono">{a.ip || "—"}</span>
                    {a.detail && <span className="ml-2 text-neutral-500">· {a.detail}</span>}
                  </p>
                  {a.user_agent && (
                    <p className="text-[10px] text-neutral-600 mt-0.5 truncate font-mono">{a.user_agent}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <Pagination skip={skip} limit={limit} total={data.total || 0} onChange={setSkip} />
        </Card>
      )}
    </div>
  );
};

export default LoginAttempts;
